import type { TemplatePage } from '../templates';

const faq = [
  {
    q: 'What should a cancellation policy for a service business include?',
    a: 'How much notice a customer must give to cancel or move an appointment, what happens when they give less, what happens if your crew cannot get in, what happens to any deposit, what you do when you are the one who has to cancel, and how the customer should contact you.',
  },
  {
    q: 'How much notice should I ask for?',
    a: 'There is no single right answer. It depends on how easily you can fill a cancelled slot, how far your crews travel and what your local customers expect. The template leaves the number blank for you to choose.',
  },
  {
    q: 'Can I charge a fee when a customer locks us out?',
    a: 'Many service businesses include a lockout or no-access charge in their policy. Whether and how you can charge it depends on your agreement with the customer and the rules where you work, so treat the template as a starting point and check what applies to your business.',
  },
  {
    q: 'How do I make sure customers actually agree to the policy?',
    a: 'Put it on the quote they accept, not only on your website. In Scopie your cancellation terms can be saved as a clause, so they appear on every quote the customer opens and signs.',
  },
  {
    q: 'Is this template free?',
    a: 'Yes. Copy it, download the PDF and change it however you like. There is no sign-up.',
  },
];

const page: TemplatePage = {
  slug: 'cancellation-policy',
  path: '/templates/cancellation-policy',
  primaryKeyword: 'cleaning service cancellation policy template',
  secondaryKeywords: ['cancellation policy template for service business', 'contractor cancellation policy', 'lockout fee policy'],
  title: 'Cancellation Policy Template for Service Businesses | Scopie', // 59
  metaDescription:
    'A free cancellation policy template for cleaning and home service businesses: notice, late changes, lockouts, deposits and rescheduling, in plain words.',
  h1: 'Cancellation policy template for service businesses',
  answerFirst:
    'A cancellation policy tells customers how much notice to give, what a late change or a locked door costs, what happens to a deposit, and what you do if you have to cancel. Copy the free template below, choose your own terms for each blank, and put the finished policy on every quote.',
  lastReviewed: '2026-10-06',
  schemaType: 'CreativeWork',
  disclaimer:
    'This template is general information, not legal advice. Rules on deposits, cancellation fees and consumer contracts vary by state and by type of work. Check what applies to your business before you use it.',
  blocks: [
    {
      type: 'callout',
      id: 'disclaimer',
      text: 'Not legal advice: this is a starting point to adapt. Rules on deposits and cancellation charges differ by state and by type of service, so check what applies to you.',
    },
    {
      type: 'document',
      id: 'template',
      h2: 'The policy',
      intro: 'Every value in [brackets] is yours to choose. Delete any section you do not need.',
      pdfName: 'cancellation-policy-template',
      sections: [
        { heading: 'Changing or cancelling your appointment', lines: ['Please let us know at least [number] hours before your scheduled start if you need to cancel or reschedule.', 'You can reach us by [phone / text / email / replying to your quote link].'] },
        { heading: 'Late changes', lines: ['If you cancel or reschedule with less notice than that, we charge [a fixed amount / the deposit / a share of the job price that you state].'] },
        { heading: 'If we cannot get in', lines: ['If we arrive at the scheduled time and cannot access the property, and cannot reach you within [number] minutes, we will treat the visit as a late cancellation.', 'Please make sure [keys / codes / gates / pets] are arranged before we arrive.'] },
        { heading: 'Deposits', lines: ['Deposits are [refundable / not refundable / refundable if you cancel at least (number) days before the start].', 'For jobs where we order materials for you, [your rule for materials already ordered].'] },
        { heading: 'When we have to reschedule', lines: ['Weather, illness or equipment failure can mean we need to move your appointment. If that happens we will contact you as early as we can and offer the next available time at no extra charge.'] },
        { heading: 'Rescheduling limits', lines: ['[If you have a limit, for example on how many times an appointment can be moved, state it here.]'] },
        { heading: 'Agreement', lines: ['By accepting your quote, you agree to this policy.'] },
      ],
    },
    {
      type: 'list',
      id: 'trades',
      h2: 'Adjustments by type of business',
      style: 'bullets',
      intro: 'The same structure works across trades; what changes is what a late cancellation actually costs you.',
      items: [
        { title: 'House cleaning', text: 'Lockouts and pets are the big ones. Ask for entry instructions in advance and say what happens if the code fails.' },
        { title: 'Pressure washing and window cleaning', text: 'Weather moves most outdoor appointments. Make it clear that a weather move by you is free.' },
        { title: 'Landscaping, fencing and flooring', text: 'Materials are bought for the job. Your deposit section matters more than your notice period.' },
        { title: 'Junk removal', text: 'Same-day work is common. Decide whether same-day bookings follow the normal notice rule.' },
      ],
    },
    {
      type: 'list',
      id: 'writing',
      h2: 'Tips for a policy customers will accept',
      style: 'checklist',
      items: [
        { text: 'Use plain words; avoid sounding like a penalty clause.' },
        { text: 'State one number for each rule, so nobody has to interpret it.' },
        { text: 'Say what you do when you cancel, not only what the customer owes.' },
        { text: 'Keep it short enough to read on a phone screen.' },
        { text: 'Apply it consistently; a policy you waive for everyone stops being a policy.' },
      ],
    },
    {
      type: 'text',
      id: 'on-the-quote',
      h2: 'Put it where customers will read it',
      lead: '"I never saw a cancellation policy."',
      paragraphs: [
        'A policy page on your website is easy to miss and hard to point to later. The place a customer actually reads terms is the quote they accept. In Scopie you save this policy once as a clause, and it appears on every quote, alongside the price, on the page the customer signs. When a lockout happens, the policy is part of what they agreed to.',
      ],
    },
    { type: 'faq', id: 'faq', h2: 'Cancellation policy questions', items: faq },
  ],
  faq,
  internalLinks: [
    { href: '/for/cleaning', anchor: 'Scopie for cleaning businesses', status: 'live' },
    { href: '/for/pressure-washing', anchor: 'Scopie for pressure washing businesses', status: 'live' },
    { href: '/templates/flooring-estimate-template', anchor: 'flooring estimate template', status: 'live' },
  ],
  cta: {
    heading: 'Make your policy part of every quote',
    text: 'Save this policy as a clause in Scopie and it rides along on each quote your customers sign.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
};

export default page;
